import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { bucketsQuery } from "@visioni/a3s-traffic-flow/app";
import { useTenantId } from "@src/features/tenants";
import type { Period } from "../utils/summarise-buckets";

const PERIOD_DAYS: Record<Period, number> = {
  "1d": 1,
  "3d": 3,
  "7d": 7,
  "30d": 30,
};

const ONE_MINUTE_MS = 60_000;

function periodToRange(period: Period): { from: string; to: string } {
  const nowMs = Math.floor(Date.now() / ONE_MINUTE_MS) * ONE_MINUTE_MS;
  const to = new Date(nowMs).toISOString();
  const from = new Date(nowMs - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000).toISOString();
  return { from, to };
}

export function useBuckets(siteId: SiteId, assetCameraKey: AssetCameraKey, scenarioUuid: string, period: Period) {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { from, to } = useMemo(() => periodToRange(period), [period]);

  return useQuery({
    ...bucketsQuery(apiConfig, { siteId, assetCameraKey, scenarioUuid }, { from, to }),
    enabled: !!tenantId && !!scenarioUuid,
    staleTime: ONE_MINUTE_MS,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}
